import React, { useState, useEffect } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useWordsData } from '../../lib/useWordsData';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import { useWordAudio } from '../../lib/useWordAudio';
import './StorySequence.css';

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/stories';

const IMAGE_MAP = {
  'milk_1': 'milk_1', 'milk_2': 'milk_2', 'milk_3': 'milk_3',
  'vel_1': 'vel_1', 'vel_2': 'vel_2', 'vel_3': 'vel_3',
  'bunny_1': 'bunny_1', 'bunny_2': 'bunny_2', 'bunny_3': 'bunny_3', 'bunny_4': 'bunny_4',
  'Kormushki_1': 'Kormushki_1', 'Kormushki_2': 'Kormushki_2', 'Kormushki_3': 'Kormushki_3', 'Kormushki_4': 'Kormushki_4',
  'Losi_1': 'Losi_1', 'Losi_2': 'Losi_2', 'Losi_3': 'Losi_3', 'Losi_4': 'Losi_4', 'Losi_5': 'Losi_5',
  'svet_1': 'svet_1', 'svet_2': 'svet_2', 'svet_3': 'svet_3', 'svet_4': 'svet_4', 'svet_5': 'svet_5'
};

const STORIES_BY_AGE = {
  '4': ['girl_cat_story', 'boy_bike_story'],
  '5': ['bunny_story', 'feeders_story'],
  '6': ['moose_story', 'firefly_story'],
  '7-9': ['moose_story', 'firefly_story'], 
  '10-12': [], '13-15': [], '16+': []
};

const StorySequence = ({ ageGroup, onBack, onComplete }) => {
  const { data: wordsData, loading: wordsLoading } = useWordsData();
  const { play: playInstruction } = useInstructionAudio();
  const { play: playWordAudio } = useWordAudio();
  const INSTRUCTION = "Расставь картинки по порядку. Нажми на динамик, чтобы послушать картинку.";
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [items, setItems] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [storyLog, setStoryLog] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);

  const storyKeys = STORIES_BY_AGE[ageGroup] || [];
  const totalExercises = storyKeys.length;

  const stories = wordsData
    ? storyKeys.filter(key => wordsData.stories[key]).map(key => ({ id: key, steps: wordsData.stories[key].steps }))
    : [];

  const currentStory = stories[currentStoryIndex];

  useEffect(() => {
    if (currentStory) {
      const shuffled = [...currentStory.steps].sort(() => Math.random() - 0.5);
      setItems(shuffled);
      setIsComplete(false);
      setSelectedIndex(null);
      setDragIndex(null);

      const timer = setTimeout(() => {
        playInstruction(INSTRUCTION);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentStoryIndex, wordsData]);

  useEffect(() => {
    if (totalExercises === 0 && !showResult) {
        if (onComplete) onComplete({ testId: 'StorySequence', score: 0 });
        return;
    }

    if (showResult) {
      const timer = setTimeout(() => {
        const correctCount = answers.filter(a => a === true).length;
        const score = totalExercises > 0 ? Math.round((correctCount / totalExercises) * 100) : 0;
        if (onComplete) {
          onComplete({
            testId: 'StorySequence',
            score: score,
            details: { total: totalExercises, correct: correctCount, stories: storyLog },
          });
        } else {
          onBack();
        }
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showResult, totalExercises]);

  const swapItems = (index1, index2) => {
    const newItems = [...items];
    [newItems[index1], newItems[index2]] = [newItems[index2], newItems[index1]];
    setItems(newItems);
  };

  const handleCardClick = (clickedIndex) => {
    if (isComplete) return;
    if (selectedIndex === null) setSelectedIndex(clickedIndex);
    else if (selectedIndex === clickedIndex) setSelectedIndex(null);
    else { swapItems(selectedIndex, clickedIndex); setSelectedIndex(null); }
  };

  const handleDragStart = (e, index) => {
    if (isComplete) return;
    setSelectedIndex(null);
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === targetIndex) { setDragIndex(null); return; }
    swapItems(dragIndex, targetIndex);
    setDragIndex(null);
  };

  const handleDragEnd = () => setDragIndex(null);

  const handleSpeak = (e, audioUrl, id) => {
    e.stopPropagation();
    if (speakingId === id) return;
    setSpeakingId(id);
    playWordAudio(audioUrl, () => setSpeakingId(null));
  };

  const handleCheck = () => {
    if (isComplete) return;
    const correctPositions = items.filter((item, index) => item.id === currentStory.steps[index].id).length;
    const totalPositions = currentStory.steps.length;
    const isCorrect = correctPositions === totalPositions;

    setAnswers([...answers, isCorrect]);
    setStoryLog(prev => [...prev, { storyId: currentStory.id, correctPositions, totalPositions, isCorrect }]);
    setIsComplete(true);
    setTimeout(() => {
      if (currentStoryIndex < totalExercises - 1) setCurrentStoryIndex(currentStoryIndex + 1);
      else setShowResult(true);
    }, 1500);
  };

  const getImageSrc = (id) => `${S3_BASE_URL}/${IMAGE_MAP[id] || 'placeholder'}.png`;

  if (showResult) {
    const correctCount = answers.filter(a => a === true).length;
    return (
      <ModuleTemplate progress={answers} totalQuestions={totalExercises} forceStop={true} onComplete={onComplete || onBack}>
        <div className="story-result-content">
          <h2 className="story-result-title">🎉 Упражнение завершено!</h2>
          <div className="story-result-score">
            <span className="story-score-number">{correctCount}</span>
            <span className="story-score-total"> / {totalExercises}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (wordsLoading) {
    return (
      <ModuleTemplate progress={answers} totalQuestions={0} forceStop={true} onComplete={onComplete || onBack}>
        <div className="story-loading-text">Загрузка...</div>
      </ModuleTemplate>
    );
  }

  if (!currentStory || totalExercises === 0) {
    return (
      <ModuleTemplate progress={answers} totalQuestions={0} forceStop={true} onComplete={onComplete || onBack}>
        <div className="story-loading-text">Для этого возраста пока нет заданий</div>
      </ModuleTemplate>
    );
  }

  return (
    <ModuleTemplate 
      progress={answers} 
      totalQuestions={totalExercises} 
      forceStop={true} 
      onComplete={onComplete || onBack} 
      extraContent={INSTRUCTION}
      extraButtons={
        !isComplete ? (
          <button className="negation-confirm-btn" onClick={handleCheck}>ОК</button>
        ) : null
      }
    >
      <div className="story-items-row">
        {items.map((item, index) => {
          let cardStatusClass = '';
          if (isComplete) cardStatusClass = item.id === currentStory.steps[index].id ? 'correct-card' : 'wrong-card';
          return (
            <div key={item.id} className="story-slot-wrapper">
              <div className="story-pos-num">{index + 1}</div>
              <div
                className={`story-item-card ${selectedIndex === index ? 'selected-card' : ''} ${dragIndex === index ? 'dragging' : ''} ${cardStatusClass}`}
                onClick={() => handleCardClick(index)}
                draggable={!isComplete}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                <img src={getImageSrc(item.id)} alt="" className="story-item-img" onError={(e)=>e.target.src='/placeholder.png'} />
                <button className={`story-speak-btn ${speakingId === item.id ? 'active' : ''}`} onClick={(e) => handleSpeak(e, item.audio_url, item.id)} draggable={false}>🔊</button>
              </div>
            </div>
          );
        })}
      </div>
    </ModuleTemplate>
  );
};

export default StorySequence;